import { useState } from 'react';
import { ArrowLeft, Droplets, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { healthDataService, SugarReadingDto } from '../services/health-data.service';
import { LoadingSkeleton } from './LoadingSkeleton';
import toast from 'react-hot-toast';
import { handleApiError } from '../lib/api-client';

interface GlucoseTrackerProps {
  onBack: () => void;
}

export function GlucoseTracker({ onBack }: GlucoseTrackerProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [value, setValue] = useState('');
  const [mealContext, setMealContext] = useState<'FASTING' | 'BEFORE_MEAL' | 'AFTER_MEAL' | 'RANDOM'>('FASTING');
  const [notes, setNotes] = useState('');
  const [showForm, setShowForm] = useState(false);

  // Fetch glucose readings
  const {
    data: readingsData,
    isLoading: readingsLoading,
    error: readingsError,
  } = useQuery({
    queryKey: ['sugar-readings', user?.id],
    queryFn: () => healthDataService.getSugarReadings({ limit: 50 }),
    enabled: !!user,
  });

  const readings = readingsData?.data || readingsData || [];

  // Create reading mutation
  const createMutation = useMutation({
    mutationFn: (data: SugarReadingDto) => healthDataService.createSugarReading(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sugar-readings'] });
      toast.success('Glucose reading logged successfully!');
      setValue('');
      setNotes('');
      setShowForm(false);
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!value || isNaN(Number(value))) {
      toast.error('Please enter a valid glucose value');
      return;
    }

    createMutation.mutate({
      reading: Number(value),
      mealContext,
      notes: notes.trim() || undefined,
    });
  };

  const getGlucoseStatus = (value: number) => {
    if (value < 70) return { label: 'Low', color: 'text-red-600', bg: 'bg-red-50' };
    if (value > 180) return { label: 'High', color: 'text-orange-600', bg: 'bg-orange-50' };
    return { label: 'Normal', color: 'text-green-600', bg: 'bg-green-50' };
  };

  if (readingsLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="flex items-center space-x-2 text-gray-700 hover:text-dropsugar-green transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back</span>
            </button>
            <h1 className="text-xl font-bold text-gray-900">Glucose Tracking</h1>
            <div className="w-20" />
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {!showForm ? (
          <button
            onClick={() => setShowForm(true)}
            className="w-full bg-gradient-to-r from-dropsugar-blue to-dropsugar-blue-dark text-white py-4 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all mb-8"
          >
            + Add Glucose Reading
          </button>
        ) : (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
            <h2 className="text-xl font-bold mb-4">New Glucose Reading</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Glucose Value (mg/dL)
                </label>
                <input
                  type="number"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder="Enter glucose value"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dropsugar-green focus:border-transparent"
                  required
                  min="0"
                  max="600"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Meal Context
                </label>
                <select
                  value={mealContext}
                  onChange={(e) => setMealContext(e.target.value as any)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dropsugar-green focus:border-transparent"
                >
                  <option value="FASTING">Fasting</option>
                  <option value="BEFORE_MEAL">Before Meal</option>
                  <option value="AFTER_MEAL">After Meal</option>
                  <option value="RANDOM">Random</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any notes..."
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dropsugar-green focus:border-transparent"
                />
              </div>

              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="flex-1 bg-dropsugar-green hover:bg-dropsugar-green-dark text-white py-2 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {createMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Reading'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setValue('');
                    setNotes('');
                  }}
                  className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-semibold transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-xl font-bold mb-4 flex items-center">
            <Droplets className="w-6 h-6 mr-2 text-dropsugar-green" />
            Recent Readings
          </h2>
          {readings.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No readings yet. Add your first reading above!</p>
          ) : (
            <div className="space-y-3">
              {readings.map((reading: any) => {
                const status = getGlucoseStatus(reading.value || reading.reading);
                const date = new Date(reading.readingTime || reading.createdAt);
                return (
                  <div
                    key={reading.id}
                    className={`p-4 rounded-lg border ${status.bg} border-gray-200`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center space-x-3">
                          <span className={`text-2xl font-bold ${status.color}`}>
                            {reading.value || reading.reading} mg/dL
                          </span>
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${status.color} ${status.bg}`}>
                            {status.label}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {reading.mealContext?.replace('_', ' ')} • {date.toLocaleDateString()} {date.toLocaleTimeString()}
                        </p>
                        {reading.notes && (
                          <p className="text-sm text-gray-500 mt-1">{reading.notes}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
